import React, { useState, useEffect } from 'react';
import { auth, db } from '../services/firebase';
import { collection, query, orderBy, onSnapshot, doc, updateDoc, increment, writeBatch, serverTimestamp } from 'firebase/firestore';
import {
    Box, Typography, Card, CardContent, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, Chip, Button, IconButton, Collapse, Grid
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DescriptionIcon from '@mui/icons-material/Description';
import styles from '../styles/Dashboard.module.css';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';

const OrderRow = ({ order }) => {
    const [open, setOpen] = useState(false);

    const handleReceive = async () => {
        if (!window.confirm("Confirmar recebimento dos itens? O estoque será atualizado.")) return;

        try {
            const batch = writeBatch(db);

            // 1. Update Order Status
            const orderRef = doc(db, 'tb_ordens_compra', order.id);
            batch.update(orderRef, {
                status: 'recebido',
                data_recebimento: serverTimestamp()
            });

            // 2. Update Inventory
            order.itens.forEach(item => {
                if (item.item_id && !item.is_manual) { // Only update real items
                    const itemRef = doc(db, 'tb_estoque', item.item_id);
                    batch.update(itemRef, {
                        quantidade: increment(parseFloat(item.quantidade) || 0)
                    });
                }
            });

            await batch.commit();
            alert("Ordem recebida e estoque atualizado!");
        } catch (error) {
            console.error("Erro ao receber ordem", error);
            alert("Erro ao processar recebimento.");
        }
    };

    const handleCancel = async () => {
        if (!window.confirm("Cancelar esta ordem de compra?")) return;
        try {
            await updateDoc(doc(db, 'tb_ordens_compra', order.id), { status: 'cancelado' });
        } catch (error) {
            console.error("Erro ao cancelar", error);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'pendente': return 'warning';
            case 'recebido': return 'success';
            case 'cancelado': return 'error';
            default: return 'default';
        }
    };

    const totalOrder = order.itens ? order.itens.reduce((acc, item) => acc + ((parseFloat(item.quantidade) || 0) * (parseFloat(item.preco_unitario) || 0)), 0) : 0;

    // Logic for Layout (Consolidated vs Rateio)
    const localItems = order.itens || [];

    // 1. Consolidated (By Item Name+Unit)
    const consolidated = {};
    localItems.forEach((item) => {
        const key = `${item.nome}-${item.unidade}`;
        if (!consolidated[key]) {
            consolidated[key] = {
                key,
                nome: item.nome,
                unidade: item.unidade,
                totalQty: 0,
                price: parseFloat(item.preco_unitario) || 0,
                total: 0
            };
        }
        const qty = parseFloat(item.quantidade) || 0;
        consolidated[key].totalQty += qty;
        consolidated[key].total += (qty * consolidated[key].price);
    });

    // 2. Rateio (By Condo)
    const rateio = {};
    localItems.forEach(item => {
        const cid = item.condominio_id || 'unknown';
        const cName = item.condominio_nome || 'Desconhecido';
        if (!rateio[cid]) {
            rateio[cid] = { name: cName, total: 0, itens: 0 };
        }
        const qty = parseFloat(item.quantidade) || 0;
        const price = parseFloat(item.preco_unitario) || 0;
        rateio[cid].itens += qty;
        rateio[cid].total += (qty * price);
    });


    return (
        <>
            <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                <TableCell>
                    <IconButton size="small" onClick={() => setOpen(!open)}>
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell>{order.data_criacao?.seconds ? format(new Date(order.data_criacao.seconds * 1000), 'dd/MM/yyyy HH:mm') : '-'}</TableCell>
                <TableCell>{order.fornecedor_nome}</TableCell>
                <TableCell>
                    {totalOrder.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </TableCell>
                <TableCell>
                    <Chip label={order.status.toUpperCase()} color={getStatusColor(order.status)} size="small" />
                </TableCell>
                <TableCell align="right">
                    {order.cotacao_token && (
                        <Button
                            size="small"
                            variant="text"
                            startIcon={<DescriptionIcon />}
                            component={Link}
                            to={`/cotacao/${order.cotacao_token}`}
                            target="_blank"
                            sx={{ mr: 1 }}
                        >
                            Ver Cotação
                        </Button>
                    )}

                    {order.status === 'pendente' && (
                        <>
                            <Button
                                size="small"
                                variant="contained"
                                color="success"
                                startIcon={<CheckCircleIcon />}
                                onClick={handleReceive}
                                sx={{ mr: 1 }}
                            >
                                Receber
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                startIcon={<CancelIcon />}
                                onClick={handleCancel}
                            >
                                Cancelar
                            </Button>
                        </>
                    )}
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 2 }}>
                            <Grid container spacing={4}>
                                {/* LEFT: Consolidated Items */}
                                <Grid item xs={12} md={7}>
                                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                        1. Itens do Pedido (Consolidado)
                                    </Typography>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Item</TableCell>
                                                <TableCell align="right">Qtd Total</TableCell>
                                                <TableCell align="right">Preço Unit.</TableCell>
                                                <TableCell align="right">Subtotal</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {Object.values(consolidated).map((group, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>{group.nome} ({group.unidade})</TableCell>
                                                    <TableCell align="right">{group.totalQty}</TableCell>
                                                    <TableCell align="right">{group.price.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</TableCell>
                                                    <TableCell align="right">{group.total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Grid>

                                {/* RIGHT: Rateio Distribution */}
                                <Grid item xs={12} md={5}>
                                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                        2. Rateio por Condomínio
                                    </Typography>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Condomínio</TableCell>
                                                <TableCell align="right">Itens</TableCell>
                                                <TableCell align="right">Valor Total</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {Object.keys(rateio).map(cid => (
                                                <TableRow key={cid}>
                                                    <TableCell>{rateio[cid].name}</TableCell>
                                                    <TableCell align="right">{rateio[cid].itens}</TableCell>
                                                    <TableCell align="right">{rateio[cid].total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Grid>
                            </Grid>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

const GerenciarOrdens = () => {
    const [orders, setOrders] = useState([]);

    useEffect(() => {
        const q = query(collection(db, 'tb_ordens_compra'), orderBy('data_criacao', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setOrders(list);
        });
        return () => unsubscribe();
    }, []);

    return (
        <Box component="main" sx={{ flexGrow: 1, p: 3, background: 'linear-gradient(45deg, #1a237e 30%, #283593 90%)', minHeight: '100vh' }}>
            <Typography variant="h4" gutterBottom sx={{ color: 'white' }}>Ordens de Compra</Typography>
            <Card className={styles.card}>
                <CardContent>
                    <TableContainer component={Paper}>
                        <Table aria-label="collapsible table">
                            <TableHead>
                                <TableRow>
                                    <TableCell />
                                    <TableCell>Data</TableCell>
                                    <TableCell>Fornecedor</TableCell>
                                    <TableCell>Valor Total</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="right">Ações</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {orders.map((order) => (
                                    <OrderRow key={order.id} order={order} />
                                ))}
                                {orders.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center">Nenhuma ordem encontrada.</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>
        </Box>
    );
};

export default GerenciarOrdens;
